import React, { useState } from 'react';
import { Modal, Box, Typography, Button, Slider } from '@mui/material';
import Cropper from 'react-easy-crop';
import Compressor from 'compressorjs';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import getCroppedImg, { blobToDataURL } from '../ProfileHeaderUpload/helpers';
import { v4 as uuidv4 } from 'uuid';

interface RectangularImageCropperModalProps {
    open: boolean;
    src: string;
    onSave: (base64: string, file: File) => void;
    onCancel: () => void;
    aspect?: number;
    cropHeight?: number;
}

export const RectangularImageCropperModal: React.FC<RectangularImageCropperModalProps> = ({
    open,
    src,
    onSave,
    onCancel,
    aspect = 16 / 9,
    cropHeight = 300,
}) => {
    const { themeStyle } = useThemeContext();
    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

    const onCropComplete = (_: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleSave = async () => {
        if (!src || !croppedAreaPixels) return;
        try {
            const croppedDataUrl = await getCroppedImg(src, croppedAreaPixels);
            const result = await fetch(croppedDataUrl!);
            const blob = await result.blob();
            // Compress
            new Compressor(blob, {
                quality: 0.7,
                maxWidth: 1600,
                maxHeight: 1600,
                convertSize: 150 * 1024,
                success(file) {
                    blobToDataURL(file).then(x => {
                        const compressedFileFromBlob = new File([file], uuidv4(), {
                            type: file.type,
                        });
                        onSave(x, compressedFileFromBlob);
                    });
                },
            });
        } catch (e) {
            onCancel();
        }
    };

    return (
        <Modal open={open} onClose={onCancel} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].pure_black,
                    borderRadius: 2,
                    p: 3,
                    width: { xs: '95vw', sm: 600 },
                    maxWidth: '95vw',
                    outline: 'none',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].primary_white, mb: 2 }}>
                    Edit Cover Image
                </Typography>
                <Box sx={{ position: 'relative', width: '100%', height: cropHeight, background: '#222', mb: 2 }}>
                    <Cropper
                        image={src}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ color: theme[themeStyle].primary_white, mr: 2 }}>Zoom</Typography>
                    <Slider
                        min={1}
                        max={3}
                        step={0.01}
                        value={zoom}
                        onChange={(_, value) => setZoom(Number(value))}
                        sx={{ width: 200 }}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={onCancel} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Save
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
